import { z } from "zod";
import { AttachmentRequirementDisplaySchema } from "@/services/shared/attachment-requirement.types";
import {
	BoundingBoxSchema,
	EditJobFileInfoSchema,
	FileDisplaySchema,
	ServiceResultSchema,
} from "../job/job.types";
import { SubOrganizationDisplaySchema } from "../organization/organization.types";
import {
	type CreateNoteInfo,
	CreateNoteInfoSchema,
	type NoteDisplay,
	NoteDisplayArraySchema,
	NoteDisplaySchema,
	type NoteParams,
	NoteParamsSchema,
} from "../shared/note.types";

const optionalBooleanSchema = z.preprocess((val) => {
	if (val === "true") return true;
	if (val === "false") return false;
	return val;
}, z.boolean().optional());

// ==================== RECORD TYPE SCHEMAS ====================

/**
 * POST /records/types request body
 */
export const CreateRecordTypeInfoSchema = z.object({
	organizationId: z.string().nullable().optional(),
	subOrganizationIds: z.array(z.string()).nullable().optional(),
	name: z.string().nullable().optional(),
	description: z.string().nullable().optional(),
	icon: z.string().nullable().optional(),
});

/**
 * RecordTypeDisplay — GET /records/types/{recordTypeId}
 */
export const RecordTypeDisplaySchema = z.object({
	id: z.string().uuid(),
	name: z.string().nullable().optional(),
	description: z.string().nullable().optional(),
	icon: z.string().nullable().optional(),
	/** true = system type, false = custom type */
	defaultType: z.boolean().optional(),
	/** Whether this type is enabled for the organization */
	enabled: z.boolean().optional(),
	recordTypeSubOrganizations: z
		.array(SubOrganizationDisplaySchema)
		.optional()
		.default([]),
	createdAt: z.string().nullable().optional(),
	updatedAt: z.string().nullable().optional(),
	createdBy: z.string().uuid().nullable().optional(),
	updatedBy: z.string().uuid().nullable().optional(),
	/** Display name supplied directly by the backend — no secondary lookup needed */
	createdByUser: z.string().nullable().optional(),
	updatedByUser: z.string().nullable().optional(),
	locationRequired: z.boolean().optional(),
	attachmentRequirement: AttachmentRequirementDisplaySchema.nullable().optional(),
});

/**
 * RecordTypeDisplayTable — GET /records/types/{start}/{count} and GET /records/types
 */
export const RecordTypeDisplayTableSchema = z.object({
	id: z.string().uuid(),
	name: z.string().nullable().optional(),
	description: z.string().nullable().optional(),
	icon: z.string().nullable().optional(),
	/** true = system type, false = custom type */
	defaultType: z.boolean().optional(),
	/** Whether this type is enabled for the organization (org-scoped list) */
	enabled: optionalBooleanSchema,
	recordTypeSubOrganizations: z
		.array(SubOrganizationDisplaySchema)
		.optional()
		.default([]),
	createdAt: z.string().nullable().optional(),
	updatedAt: z.string().nullable().optional(),
	createdBy: z.string().uuid().nullable().optional(),
	updatedBy: z.string().uuid().nullable().optional(),
	/** Display name supplied directly by the backend — no secondary lookup needed */
	createdByUser: z.string().nullable().optional(),
	updatedByUser: z.string().nullable().optional(),
	locationRequired: z.boolean().optional(),
	attachmentRequirement: AttachmentRequirementDisplaySchema.nullable().optional(),
	hasForm: z.boolean().optional(),
});

export const RecordTypesArraySchema = z.array(RecordTypeDisplayTableSchema);

/**
 * PUT /records/types/{recordTypeId} request body
 */
export const EditRecordTypeInfoSchema = z.object({
	organizationId: z.string().nullable().optional(),
	subOrganizationIds: z.array(z.string()).nullable().optional(),
	name: z.string().nullable().optional(),
	description: z.string().nullable().optional(),
	icon: z.string().nullable().optional(),
	locationRequired: z.boolean().optional(),
});

// ==================== QUERY PARAM SCHEMAS ====================

/**
 * Params for GET /records/types/count and GET /records/types/{start}/{count}
 */
export const RecordTypesListParamsSchema = z.object({
	IncludeDeleted: z.boolean().optional(),
	CreatedBefore: z.string().optional(),
	CreatedAfter: z.string().optional(),
	UpdatedBefore: z.string().optional(),
	UpdatedAfter: z.string().optional(),
	OrganizationId: z.string().optional(),
	SubOrganizationId: z.string().optional(),
	ForOrganizationUser: z.string().uuid().optional(),
	Enabled: z.boolean().optional(),
	SearchTerm: z.string().optional(),
	"OrderBy.Fields": z.array(z.string()).optional(),
	"OrderBy.Descending": z.boolean().optional(),
});

/**
 * Params for GET /records/types (simple list)
 * Accepts a superset so callers can pass RecordTypesListParams directly.
 */
export const RecordTypesAllParamsSchema = z.object({
	subOrganizationId: z.string().optional(),
	OrganizationId: z.string().optional(),
});

/**
 * Params for GET /records/count — count record instances by type/org/job.
 * `BoundingBox` is flattened to dotted query keys by the record service.
 */
export const RecordCountParamsSchema = z.object({
	RecordTypeIds: z.array(z.string()).optional(),
	OrganizationId: z.string().optional(),
	SubOrganizationId: z.string().optional(),
	RelatedToJobId: z.string().optional(),
	RelatedToTicketId: z.string().optional(),
	RelatedToJobIds: z.array(z.string()).optional(),
	RelatedToTicketIds: z.array(z.string()).optional(),
	RelatedToAssetId: z.string().optional(),
	HasCoordinates: z.boolean().optional(),
	BoundingBox: BoundingBoxSchema.optional(),
	SearchTerm: z.string().optional(),
	IncludeDeleted: z.boolean().optional(),
	CreatedBefore: z.string().optional(),
	CreatedAfter: z.string().optional(),
});

// ==================== EXPORTED TYPES ====================

export type CreateRecordTypeInfo = z.infer<typeof CreateRecordTypeInfoSchema>;
export type EditRecordTypeInfo = z.infer<typeof EditRecordTypeInfoSchema>;
export type RecordTypeDisplay = z.infer<typeof RecordTypeDisplaySchema>;
export type RecordTypeDisplayTable = z.infer<
	typeof RecordTypeDisplayTableSchema
>;
/** Row type for record type lists */
export type RecordType = RecordTypeDisplayTable;
export type RecordTypesListParams = z.infer<typeof RecordTypesListParamsSchema>;
export type RecordTypesAllParams = z.infer<typeof RecordTypesAllParamsSchema>;
export type RecordCountParams = z.infer<typeof RecordCountParamsSchema>;

// ==================== RECORD INSTANCE SCHEMAS ====================

export const RecordDisplaySchema = z.object({
	id: z.string(),
	organizationId: z.string().nullable(),
	name: z.string().nullable().optional(),
	description: z.string().nullable().optional(),
	recordNumber: z.number().int().nullable().optional(),
	typeId: z.string().nullable(),
	typeName: z.string().nullable().optional(),
	typeIcon: z.string().nullable().optional(),
	subOrganizationId: z.string().nullable().optional(),
	createdAt: z.string().nullable().optional(),
	updatedAt: z.string().nullable().optional(),
	createdAtOffset: z.string().nullable().optional(),
	updatedAtOffset: z.string().nullable().optional(),
	location: z.unknown().nullable().optional(),
	geometrySource: z.string().nullable().optional(),
	gpsMethod: z.string().nullable().optional(),
	recordingDeviceData: z.unknown().nullable().optional(),
	entityAttachmentRequirementId: z.string().uuid().nullable().optional(),
	data: z.unknown().nullable().optional(),
	formDefinition: z.unknown().optional(),
});
export type RecordDisplay = z.infer<typeof RecordDisplaySchema>;

export const RecordDisplayWithUsersSchema = RecordDisplaySchema.extend({
	createdBy: z.unknown().nullable().optional(),
	updatedBy: z.unknown().nullable().optional(),
});
export type RecordDisplayWithUsers = z.infer<
	typeof RecordDisplayWithUsersSchema
>;

export const EditRecordInfoSchema = z.object({
	organizationId: z.string().nullable().optional(),
	name: z.string().nullable().optional(),
	description: z.string().nullable().optional(),
	latitude: z.number().nullable().optional(),
	longitude: z.number().nullable().optional(),
	geometrySource: z.string().nullable().optional(),
	gpsMethod: z.string().nullable().optional(),
	formData: z.record(z.string(), z.unknown()).nullable().optional(),
});
export type EditRecordInfo = z.infer<typeof EditRecordInfoSchema>;

/**
 * OpenAPI `CreateRecordInfo` — POST /records
 */
export const CreateRecordInfoSchema = z.object({
	organizationId: z.string().nullable().optional(),
	name: z.string().nullable().optional(),
	description: z.string().nullable().optional(),
	typeId: z.string().nullable().optional(),
	latitude: z.number().nullable().optional(),
	longitude: z.number().nullable().optional(),
	geometrySource: z.string().nullable().optional(),
	gpsMethod: z.string().nullable().optional(),
	subOrganizationId: z.string().nullable().optional(),
	formData: z.record(z.string(), z.unknown()).nullable().optional(),
	recordingDeviceData: z.unknown().nullable().optional(),
	/** Exactly one of jobId / ticketId required by POST /records. */
	jobId: z.string().nullable().optional(),
	ticketId: z.string().nullable().optional(),
});
export type CreateRecordInfo = z.infer<typeof CreateRecordInfoSchema>;

/** Re-export shared file display schema for record attachments. */
export const RecordFileDisplaySchema = FileDisplaySchema;
export const RecordFileDisplayArraySchema = z.array(FileDisplaySchema);
export type RecordFileDisplay = z.infer<typeof RecordFileDisplaySchema>;

/** OpenAPI `EditRecordFileInfo` */
export const EditRecordFileInfoSchema = EditJobFileInfoSchema;
export type EditRecordFileInfo = z.infer<typeof EditRecordFileInfoSchema>;

/**
 * Query filters for record file list / count endpoints.
 */
export const RecordFileParamsSchema = z.object({
	RecordId: z.string().optional(),
	SearchTerm: z.string().optional(),
	IncludeDeleted: z.boolean().optional(),
	CreatedBefore: z.string().optional(),
	CreatedAfter: z.string().optional(),
	UpdatedBefore: z.string().optional(),
	UpdatedAfter: z.string().optional(),
	OrganizationId: z.string().optional(),
	SubOrganizationId: z.string().optional(),
	ForOrganizationUser: z.string().optional(),
	OrderBy: z
		.object({
			Fields: z.array(z.string()).optional(),
			Descending: z.boolean().optional(),
		})
		.optional(),
});
export type RecordFileParams = z.infer<typeof RecordFileParamsSchema>;

/** Re-export shared note display schema for record notes. */
export const RecordNoteDisplaySchema = NoteDisplaySchema;
export const RecordNoteDisplayArraySchema = NoteDisplayArraySchema;
export type RecordNoteDisplay = NoteDisplay;

/** Body for create / update record note endpoints. */
export type RecordCreateNoteInfo = CreateNoteInfo;
export { CreateNoteInfoSchema as RecordCreateNoteInfoSchema };

/** Query filters for record note list / count endpoints. */
export const RecordNoteParamsSchema = NoteParamsSchema;
export type RecordNoteParams = NoteParams;

export interface RecordInstanceListParams {
	RelatedToJobId?: string;
	RelatedToTicketId?: string;
	RelatedToJobIds?: string[];
	RelatedToTicketIds?: string[];
	RelatedToAssetId?: string;
	OrganizationId?: string;
	SubOrganizationId?: string;
	HasCoordinates?: boolean;
	BoundingBox?: z.infer<typeof BoundingBoxSchema>;
	SearchTerm?: string;
	IncludeDeleted?: boolean;
	RecordTypeIds?: string[];
	CreatedBefore?: string;
	CreatedAfter?: string;
	UpdatedBefore?: string;
	UpdatedAfter?: string;
	"OrderBy.Fields"?: string[];
	"OrderBy.Descending"?: boolean;
}

export { ServiceResultSchema };
export type { ServiceResult } from "../job/job.types";
